import { createContext, useCallback, useContext, useMemo, type PropsWithChildren } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserSummary } from '@shared/types';
import { authApi } from '@/lib/api';
import { trackVisitorEvent } from '@/lib/visitor-analytics';
import { queryKeys } from '@/lib/query';

interface AuthContextValue {
  user: UserSummary | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: unknown;
  login: (input: { email: string; password: string }) => Promise<UserSummary>;
  register: (input: {
    fullName: string;
    email: string;
    password: string;
    preferredLanguage: 'en' | 'ar';
    turnstileToken?: string;
  }) => Promise<UserSummary>;
  logout: () => Promise<void>;
  clearLocalAuth: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: () => authApi.me(),
    staleTime: 2 * 60_000,
    retry: 1,
  });

  const login = useCallback(
    async (input: { email: string; password: string }) => {
      const user = await authApi.login(input);
      queryClient.setQueryData(queryKeys.session, user);
      return user;
    },
    [queryClient],
  );

  const register = useCallback(
    async (input: {
      fullName: string;
      email: string;
      password: string;
      preferredLanguage: 'en' | 'ar';
      turnstileToken?: string;
    }) => {
      const user = await authApi.register(input);
      queryClient.setQueryData(queryKeys.session, user);
      trackVisitorEvent('signup_complete', '/register');
      return user;
    },
    [queryClient],
  );

  const clearLocalAuth = useCallback(() => {
    queryClient.setQueryData(queryKeys.session, null);
    queryClient.removeQueries({
      predicate: ({ queryKey }) => queryKey[0] !== 'public-config' && queryKey[0] !== 'session',
    });
  }, [queryClient]);

  const logout = useCallback(async () => {
    await authApi.logout();
    clearLocalAuth();
  }, [clearLocalAuth]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session.data ?? null,
      isLoading: session.isPending,
      isAuthenticated: Boolean(session.data),
      error: session.error,
      login,
      register,
      logout,
      clearLocalAuth,
      refresh,
    }),
    [
      clearLocalAuth,
      login,
      logout,
      refresh,
      register,
      session.data,
      session.error,
      session.isPending,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// AuthProvider and its consumer hook intentionally share this module.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
